import { NumberMaterialInput } from 'components/Custom/NumberMaterialInput'
import { Box, Slider } from 'components/Material/index'
import { useEffect, useState } from 'react'
import { Controller, Control, FieldValues, Path, UseFormSetValue, PathValue } from 'react-hook-form'

interface DoubleNumberWithSliderProps<T extends FieldValues> {
  control: Control<T>
  name: string
  label: string
  getValues: (name: string) => number
  setValue: UseFormSetValue<T>
  minValue?: number
  maxValue?: number
  adornementText?: string
  auxLabel?: string
  isSliderDisabled?: boolean
}

export const DoubleNumberWithSlider = <T extends FieldValues>({
  control,
  getValues,
  setValue,
  label,
  minValue = 0,
  maxValue = 1000000,
  auxLabel = '',
  name,
  isSliderDisabled = false
}: DoubleNumberWithSliderProps<T>) => {
  const nameInicial = (name + '_inicial') as Path<T>
  const nameFinal = (name + '_final') as Path<T>
  const currentValueInicial = getValues(nameInicial)
  const currentValueFinal = getValues(nameFinal)

  return (
    <Box className="flex flex-col gap-5">
      <Box className="label-large">{label}</Box>
      <Box className="flex gap-5">
        <NumberInputField<T>
          control={control}
          name={nameInicial}
          label={auxLabel ? 'Mínimo ' + auxLabel : 'Mínimo'}
          minValue={minValue}
          maxValue={maxValue}
          currentValueInicial={currentValueInicial}
          currentValueFinal={currentValueFinal}
        />
        <NumberInputField<T>
          control={control}
          name={nameFinal}
          label={auxLabel ? 'Máximo ' + auxLabel : 'Máximo'}
          minValue={minValue}
          maxValue={maxValue}
          currentValueInicial={currentValueInicial}
          currentValueFinal={currentValueFinal}
        />
      </Box>

      {!isSliderDisabled && (
        <Box className="flex justify-center px-5 pt-4">
          <RangeSlider<T>
            getValues={getValues}
            setValue={setValue}
            nameInicial={nameInicial}
            nameFinal={nameFinal}
            minValue={minValue}
            maxValue={maxValue}
          />
        </Box>
      )}
    </Box>
  )
}

interface NumberInputFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label: string
  minValue?: number
  maxValue?: number
  currentValueInicial: number
  currentValueFinal: number
}

function NumberInputField<T extends FieldValues>({
  control,
  name,
  label,
  maxValue,
  minValue,
  currentValueInicial,
  currentValueFinal
}: NumberInputFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <NumberMaterialInput
            {...field}
            label={label}
            value={field.value}
            onChange={(value) => {
              field.onChange(value)
            }}
            fullWidth
            type={'integer'}
            max={maxValue}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              const { value } = e.target
              const numericValue = value.replace(/\./g, '').replace(',', '.')
              const numericValueAsNumber = parseFloat(numericValue || '0')
              const isInvalidValue =
                numericValueAsNumber < (minValue ?? 0) ||
                numericValueAsNumber > (maxValue ?? Number.MAX_SAFE_INTEGER)

              if (name.includes('inicial')) {
                if (isInvalidValue || numericValueAsNumber > currentValueFinal) {
                  field.onChange(minValue)
                }
              } else if (name.includes('final')) {
                if (isInvalidValue || numericValueAsNumber < currentValueInicial) {
                  field.onChange(maxValue)
                }
              }
            }}
          />
        )
      }}
    />
  )
}

interface RangeSliderProps<T extends FieldValues> {
  getValues: (name: string) => number
  setValue: UseFormSetValue<T>
  nameInicial: Path<T>
  nameFinal: Path<T>
  minValue?: number
  maxValue?: number
}
function RangeSlider<T extends FieldValues>({
  getValues,
  setValue,
  nameInicial,
  nameFinal,
  minValue = 0,
  maxValue = 1000000
}: RangeSliderProps<T>) {
  const valueInitial = getValues(nameInicial)
  const valueFinal = getValues(nameFinal)
  const [value, setValueState] = useState<number[]>([valueInitial, valueFinal])
  useEffect(() => {
    const updateValues = () => {
      if (valueInitial >= minValue && valueFinal <= maxValue) {
        const updatedInitial = getValues(nameInicial)
        const updatedFinal = getValues(nameFinal)
        setValueState([updatedInitial, updatedFinal])
      }
    }
    updateValues()
  }, [getValues, nameInicial, nameFinal, valueInitial, valueFinal])

  const handleChange = (_: Event, newValue: number | number[]) => {
    const newValues = newValue as PathValue<T, Path<T>>
    setValueState(newValues)
    setValue(nameInicial, newValues[0])
    setValue(nameFinal, newValues[1])
  }

  return (
    <Slider
      value={value}
      onChange={handleChange}
      valueLabelDisplay="auto"
      min={minValue}
      max={maxValue}
    />
  )
}
